<template>
  <section id="color" class="c__section section__color">
    <div class="c__container">
      <div class="c__headblock">
        <h2 class="c__leadcopy navy c__no__anm">
          好きな色で<br />
          おうち時間を楽しむ
        </h2>
        <p class="navy c__bodycopy c__no__anm">
          ご自宅での時間を楽しく過ごせるように、<br />
          豊富なカラーをお選びいただけます。
        </p>
      </div>
      <div class="color__wrap">
        <div class="color__container">
          <select
            id="pajama_material"
            :value="selectedProductId"
            name="pajama_material"
            class="parts__select"
            @change="onChangeProduct(($event.target as HTMLSelectElement).value)"
          >
            <option
              v-for="product in productsWithColors"
              :key="`product-${product.document_id}`"
              :value="product.document_id"
            >
              {{ product.name }}
            </option>
          </select>
          <!-- 高さが崩れないようにローディングを表示 -->
          <div v-if="productsWithColors.length === 0" class="color__img__wrap">
            <div class="color__slider__container">
              <div class="slider__pc">
                <div class="color__slider">
                  <AtomsConsumerLoading />
                </div>
              </div>
              <div class="slider__sp">
                <div class="color__slider">
                  <AtomsConsumerLoading />
                </div>
              </div>
            </div>
            <figure class="main">
              <AtomsConsumerLoading />
            </figure>
          </div>
          <!-- 商品のカラー情報を表示、商品を切り替えた時に一瞬崩れてしまわないようにv-showで切り替えている -->
          <div v-show="productsWithColors.length > 0">
            <FeaturesHowtochooseRecoverywearSleepitemPartsColorItem
              v-for="product in productsWithColors"
              v-show="product.document_id === selectedProductId"
              :key="`product-${product.document_id}`"
              :products-with-color="product"
              :selected-color-index="selectedColorIndex[product.document_id] ?? 0"
              @change="onChangeColor(product, $event)"
            />
          </div>
        </div>
        <AtomsConsumerLink :url="productUrl" class="c__btn c__fit-content c__anm detail__btn c__no__anm">
          <p>商品を見る</p>
        </AtomsConsumerLink>
      </div>
    </div>
    <div class="sticky__height"></div>
  </section>
</template>

<script setup lang="ts">
import { getSiteProductPageUrl } from "~/utils/functions/product"
import type {
  BakuneTopProduct,
  ProductsWithColor,
  SelectedColor,
} from "~/utils/functions/howtochoose_recoverywear_sleepitem"

const props = defineProps<{
  products: BakuneTopProduct[]
}>()

const selectedProductId = ref<string>("")
const selectedColorIndex = ref<Record<string, number>>({})
const sessionKey = "howtochoose_recoverywear_sleepitem_color"

/**
 * 商品のカラー情報を使用しやすいように成形する
 */
const productsWithColors = computed((): ProductsWithColor[] => {
  return props.products
    .slice()
    .sort((a, b) => (a.colorSort ?? 0) - (b.colorSort ?? 0))
    .map((product) => {
      const colors: SelectedColor[] = []
      product.skus?.forEach((sku) => {
        const existSameColorSelect = colors.some(
          (select) => typeof select.color === "string" && select.color === sku.color,
        )
        if (!existSameColorSelect) {
          colors.push({
            product_id: product.document_id,
            color: sku.color ?? "",
            size: sku.size,
            color_code: sku.color_code ?? "",
            main_image: sku.main_image ?? "",
          })
        }
      })

      let name = product.name

      if (product.detail === "crew-neck") {
        name += "(クルーネック)"
      } else if (product.detail === "mock-neck") {
        name += "(モックネック)"
      } else if (product.detail === "jogger-pants") {
        name += "(ジョガーパンツ)"
      } else if (product.detail === "tapered-pants") {
        name += "(テーパードパンツ)"
      }

      if (product.sleevesAndPants === "short-sleeve-short-pants") {
        name += " 半袖"
      } else if (product.sleevesAndPants === "long-sleeve-long-pants") {
        name += " 長袖"
      } else if (product.silhouette === "one-piece") {
        name += " ワンピース"
      }

      return {
        document_id: product.document_id,
        name,
        colors,
      }
    })
})

const productUrl = computed(() => {
  const product = props.products.find((product) => product.document_id === selectedProductId.value) ?? props.products[0]
  return product ? getSiteProductPageUrl(product) : ""
})

const onChangeProduct = (value: string) => {
  selectedProductId.value = value
  // 選択した商品とカラーをセッションストレージに保存
  sessionStorage.setItem(
    sessionKey,
    JSON.stringify({ product_id: value, color_index: selectedColorIndex.value[value] ?? 0 }),
  )
}

const onChangeColor = (product: ProductsWithColor, index: number) => {
  selectedColorIndex.value[product.document_id] = index
  // 選択した商品とカラーをセッションストレージに保存
  sessionStorage.setItem(sessionKey, JSON.stringify({ product_id: product.document_id, color_index: index }))
}

onMounted(() => {
  // 選択している商品とカラーをセッションストレージから取得
  if (sessionStorage.getItem(sessionKey)) {
    const savedSelectedColor = JSON.parse(sessionStorage.getItem(sessionKey) ?? "{}")
    selectedColorIndex.value = { [savedSelectedColor.product_id]: savedSelectedColor.color_index }
    selectedProductId.value = savedSelectedColor.product_id
  }
})

watch(
  () => productsWithColors.value,
  () => {
    if (!selectedProductId.value) {
      selectedProductId.value = productsWithColors.value[0]?.document_id ?? ""
    }
  },
  { deep: true },
)
</script>

<style scoped lang="scss">
@import "assets/scss/pages/howtochoose_recoverywear_sleepitem/main";
</style>
